import { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { PlusCircleIcon, ChatCircleIcon } from '@phosphor-icons/react'
import { AppButton, AppForm, AppFormItem, AppTextArea, useAppForm } from '@/shared/ui'
import { APC_MODE } from '../../enumerations/admin-product-card.enumeration'

type ProductCommentLabels = {
  [key: string]: string
}

/**
 * Label texts.
 */
const LABELS: ProductCommentLabels = {
  comment: 'Comment',
  optional: 'Optional',
  writeCommentHere: 'Please write comment here',
  save: 'Save',
  cancel: 'Cancel',
  addComment: 'Add comment'
}

export type ProductCommentProps = {
  show?: boolean
  text?: string
  icon?: ReactNode
  loading?: boolean
  mode?: APC_MODE
  className?: string
  sendComment?: (comment: string) => void
  cancelComment?: () => void
  onToggleForm?: (show?: boolean) => void
  copy?: (text: string) => string
}

export const ProductComment: FunctionComponent<ProductCommentProps> = ({
  show = true,
  text,
  loading,
  mode,
  icon = <PlusCircleIcon />,
  className = '',
  sendComment = () => {},
  cancelComment = () => {},
  onToggleForm = () => {},
  copy = (text: string): string => (LABELS[text] ? LABELS[text] : text)
}) => {
  const [commentText, setCommentText] = useState('')
  const [showForm, setShowForm] = useState(show)
  const [form] = useAppForm()

  const onFinish = ({ comment }: any) => {
    sendComment(comment)
  }

  const onValuesChange = (values: any) => {
    setCommentText(values.comment)
  }

  const cancelAction = () => {
    changeShowForm(false)
    cancelComment()
  }

  const isValid = () => commentText.length > 1

  const changeShowForm = (show: boolean) => {
    setShowForm(show)
    onToggleForm(show)
  }

  useEffect(() => {
    setShowForm(show)
  }, [show])

  useEffect(() => {
    setCommentText(text ?? '')
    if (showForm) {
      form.setFieldsValue({
        comment: text
      })
    }
  }, [text, showForm])

  return (
    <div className={`gungnir-product-comment ${className}`}>
      {showForm ? (
        <div className='gungnir-pc-form'>
          <p className='gungnir-pc-form-label'>
            <ChatCircleIcon />
            {copy('comment')} <span className='gungnir-pc-form-optional'>{`(${copy('optional')})`}</span>
          </p>
          <AppForm onFinish={onFinish} onValuesChange={onValuesChange} form={form}>
            <AppFormItem name='comment'>
              <AppTextArea maxLength={500} rows={2} placeholder={copy('writeCommentHere')} />
            </AppFormItem>
            <div className='comment-buttons-container'>
              <AppButton
                variant='primary'
                htmlType='submit'
                loading={loading}
                disabled={!isValid() && mode !== APC_MODE.ADD}
              >
                {copy('save')}
              </AppButton>
              <AppButton onClick={cancelAction} variant='link'>
                {copy('cancel')}
              </AppButton>
            </div>
          </AppForm>
        </div>
      ) : (
        mode !== APC_MODE.ADD && (
          <div className='gungnir-pc-comment-add'>
            <a onClick={() => changeShowForm(true)}>
              {copy('addComment')} {icon ?? ''}
            </a>
          </div>
        )
      )}
    </div>
  )
}
